import { ImageGalleryProps } from "./types"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Image as RNImage } from "~/ui/primitives"
import styled, { useTheme } from '~/ui/styled'
import FastImage from "~/ui/FastImage"
import useMediaLibrary from "~hooks/useMediaLibrary"
import useNavigation from "~hooks/useNavigation"
import { Video } from "~components/video"
import { IconFont, Button } from "~components/El"
import { useStores } from "~models"
import { getMediaPath, staticUrl, globalToast } from "~utils"
import { fitContainSize, getGalleryPreviewBounds } from "~utils/media-fit"
import { MessageTypes } from "~proto"
import { Loading } from "~widgets/loading"

const Container = styled.View`
  position: relative;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding-bottom: 24px;
  background-color: rgba(0, 0, 0, 1);
`

const DownloadButton = styled.Pressable`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1000;
  padding: 15px;
`

const PreviewArea = styled.View`
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`

const MediaFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`

const CloseButton = ({ theme, onClick, name }) => (
  <Button
    width={32}
    height={32}
    border={`1px solid ${theme.colors.border}`}
    borderRadius={16}
    activeOpacity={1.0}
    onClick={onClick}
  >
    <IconFont name={name} size={14} color={theme.colors.border} />
  </Button>
)

const Row = styled.View`
  flex-direction: row;
`

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, index = 0, onClose }) => {
  const { messageStore } = useStores()
  const theme = useTheme()
  const navigation = useNavigation()
  const { saveAssetAsync } = useMediaLibrary({ writeOnly: false })
  const [loading, setLoading] = useState(false)
  const [bounds, setBounds] = useState(getGalleryPreviewBounds)
  const [displaySize, setDisplaySize] = useState(() => {
    const b = getGalleryPreviewBounds()
    return { width: b.maxWidth, height: b.maxHeight }
  })

  const handleClose = onClose ?? (() => navigation.goBack())
  const current = images[index] ?? images[0]

  useEffect(() => {
    const updateBounds = () => setBounds(getGalleryPreviewBounds())
    window.addEventListener("resize", updateBounds)
    return () => window.removeEventListener("resize", updateBounds)
  }, [])

  const applyFit = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      setDisplaySize(fitContainSize(naturalWidth, naturalHeight, bounds.maxWidth, bounds.maxHeight))
    },
    [bounds.maxWidth, bounds.maxHeight],
  )

  useEffect(() => {
    if (!current) return
    setDisplaySize({ width: bounds.maxWidth, height: bounds.maxHeight })

    if (current.type === MessageTypes.VIDEO) return

    const uri = getMediaPath(current)
    if (!uri) return

    let cancelled = false
    RNImage.getSize(
      uri,
      (w, h) => {
        if (!cancelled) applyFit(w, h)
      },
      () => {
        if (!cancelled) {
          setDisplaySize({ width: bounds.maxWidth, height: bounds.maxHeight })
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [current, bounds.maxWidth, bounds.maxHeight, applyFit])

  const previewUri = useMemo(() => {
    if (!current?.message_id) return undefined
    const attach = messageStore.messages.get(current.message_id)?.attach
    return attach?.localVideoPreviewImageUri || attach?.previewUri
  }, [current?.message_id, messageStore.messages])

  if (!current) return null

  const onSaveAsset = async (url: string) => {
    try {
      const httpUrl = staticUrl(url)
      setLoading(true)
      await saveAssetAsync({ uri: httpUrl })
    } catch (e) {
      globalToast.show(e?.message)
    } finally {
      setLoading(false)
    }
  }

  const mediaFrameStyle = {
    width: displaySize.width,
    height: displaySize.height,
    maxWidth: bounds.maxWidth,
    maxHeight: bounds.maxHeight,
  }

  const mediaStyle = {
    width: displaySize.width,
    height: displaySize.height,
    maxWidth: bounds.maxWidth,
    maxHeight: bounds.maxHeight,
    objectFit: "contain" as const,
  }

  return (
    <Container>
      <DownloadButton onClick={() => onSaveAsset(getMediaPath(current))}>
        {loading ? <Loading /> : (
          <IconFont name="xiazai" color={theme.colors.blockBackground} size={20} />
        )}
      </DownloadButton>
      <PreviewArea>
        <MediaFrame style={mediaFrameStyle}>
          {current.type === MessageTypes.VIDEO ? (
            <Video
              previewImageUri={previewUri}
              style={mediaStyle}
              isLooping={false}
              forceStart
              src={getMediaPath(current)}
              onLoad={(size) => {
                if (size?.width && size?.height) applyFit(size.width, size.height)
              }}
            />
          ) : (
            <FastImage
              style={mediaStyle}
              src={{ uri: getMediaPath(current) }}
              resizeMode="contain"
            />
          )}
        </MediaFrame>
      </PreviewArea>
      <Row>
        <CloseButton theme={theme} name="close" onClick={handleClose} />
      </Row>
    </Container>
  )
}

export default ImageGallery
